import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Star, Quote } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK = [
  { id: "1", name: "Giuseppe R.", location: "Napoli", rating: 5, text: "Professionale, puntuale e molto competente. Ha risolto il problema elettrico in modo rapido." },
  { id: "2", name: "Maria D.", location: "Portici", rating: 5, text: "Persona affidabile, responsabile e disponibile. Ha risolto il problema tempestivamente." },
  { id: "3", name: "Antonio L.", location: "San Giorgio a Cremano", rating: 5, text: "Carlo è serio, professionale e molto preparato. Consigliatissimo." },
];

const initials = (name) =>
  name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export const Reviews = () => {
  const [reviews, setReviews] = useState(FALLBACK);
  const [stats, setStats] = useState({ average: 4.8, count: 25 });
  const trackRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((r) => Array.isArray(r.data) && r.data.length && setReviews(r.data))
      .catch(() => {});
    axios
      .get(`${API}/stats`)
      .then((r) => r.data && setStats(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (trackRef.current) {
      setWidth(trackRef.current.scrollWidth - trackRef.current.offsetWidth);
    }
  }, [reviews]);

  return (
    <section
      id="recensioni"
      className="relative overflow-hidden py-24 sm:py-32"
      data-testid="reviews-section"
    >
      <div className="mx-auto max-w-6xl px-5">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#FFC400]">
                / Recensioni
              </p>
              <h2 className="max-w-xl font-display text-3xl font-extrabold tracking-tighter text-white sm:text-5xl">
                Cosa dicono i nostri clienti
              </h2>
            </div>
            <div className="glass flex items-center gap-4 rounded-2xl px-5 py-4">
              <div>
                <p className="font-display text-4xl font-extrabold text-[#FFC400]">
                  {String(stats.average).replace(".", ",")}
                </p>
                <div className="mt-1 flex text-[#FFC400]">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-[#FFC400]" />
                  ))}
                </div>
              </div>
              <div className="h-12 w-px bg-white/10" />
              <p className="max-w-[120px] text-sm text-white/60">
                Media su oltre {stats.count} recensioni Google
              </p>
            </div>
          </div>
        </Reveal>
      </div>

      <motion.div ref={trackRef} className="mt-12 overflow-hidden px-5">
        <motion.div
          drag="x"
          dragConstraints={{ right: 0, left: -width }}
          className="flex cursor-grab gap-5 active:cursor-grabbing"
          data-testid="reviews-track"
        >
          {reviews.map((r) => (
            <div
              key={r.id}
              className="glass relative w-[300px] shrink-0 rounded-2xl p-6 sm:w-[380px]"
              data-testid="review-card"
            >
              <Quote className="mb-4 h-8 w-8 text-[#FFC400]/40" />
              <div className="mb-3 flex text-[#FFC400]">
                {[...Array(r.rating)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[#FFC400]" />
                ))}
              </div>
              <p className="min-h-[96px] text-white/85">“{r.text}”</p>
              <div className="mt-5 flex items-center gap-3 border-t border-white/10 pt-4">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-[#FFC400] font-display font-bold text-[#0B1F3A]">
                  {initials(r.name)}
                </span>
                <div>
                  <p className="font-semibold text-white">{r.name}</p>
                  <p className="text-xs text-white/50">{r.location}</p>
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>
      <p className="mt-6 px-5 text-center text-xs uppercase tracking-widest text-white/30">
        ← Trascina per esplorare →
      </p>
    </section>
  );
};

export default Reviews;
